import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useExternalLink } from './ExternalLinkWarning';
import FallbackVisitorCounter from './FallbackVisitorCounter';
import { formatDateMarathi, formatDateEnglish } from '../config/websiteConfig';
import './Footer.css';

// Helper function to get UTC date string (YYYY-MM-DD)
// Using UTC to match backend and VPS timezone
// Note: Function name kept as getISTDateString for backward compatibility
function getISTDateString() {
  const now = new Date();
  // Use UTC date components directly (matches backend)
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

const Footer = () => {
  const { isMarathi } = useLanguage();
  const navigate = useNavigate();
  const { showWarning } = useExternalLink();

  // Handle external link clicks
  const handleExternalLink = (url) => {
    showWarning(url);
  };
  const [visitorCount, setVisitorCount] = useState(0);
  const [dailyCount, setDailyCount] = useState(0);
  const [backendAvailable, setBackendAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [websiteLastUpdated, setWebsiteLastUpdated] = useState(null);

  // Fetch visitor count and last updated time with smart auto-refresh
  useEffect(() => {
    // Clean up old localStorage keys if they exist (migration from monthly/weekly to daily)
    if (localStorage.getItem('lastKnownMonthlyCount')) {
      localStorage.removeItem('lastKnownMonthlyCount');
    }
    if (localStorage.getItem('lastKnownWeeklyCount')) {
      localStorage.removeItem('lastKnownWeeklyCount');
    }
    
    let intervalId = null;
    let isTabVisible = true;
    let lastActivity = Date.now();
    const INACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes

    const fetchVisitorData = async () => {
      try {
        const API_BASE = process.env.NODE_ENV === 'production' 
          ? 'https://adminconsole.mlwb.in/api' 
          : 'http://localhost:5000/api';
        // Add timestamp to prevent caching
        const timestamp = new Date().getTime();
        const res = await fetch(`${API_BASE}/visitors/stats?t=${timestamp}`, { 
          cache: 'no-store'
        });
        
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        
        const data = await res.json();
        
        // Validate response structure
        if (typeof data !== 'object' || data === null) {
          throw new Error('Invalid API response format');
        }
        
        // Debug: Log the API response
        console.log('API Response:', data);
        console.log('API Response keys:', Object.keys(data));
        console.log('Daily value from API:', data?.daily, 'Type:', typeof data?.daily);
        
        const realCount = Number(data?.total || 0);
        const realDaily = Number(data?.daily || 0);
        
        // Validate numbers
        if (isNaN(realCount) || isNaN(realDaily)) {
          throw new Error('Invalid number format in API response');
        }
        
        // Debug: Log parsed values
        console.log('Parsed values - Total:', realCount, 'Daily:', realDaily);
        
        // ALWAYS store the real count for fallback use with UTC date
        const todayUTC = getISTDateString();
        localStorage.setItem('lastKnownVisitorCount', realCount.toString());
        localStorage.setItem('lastKnownDailyCount', realDaily.toString());
        localStorage.setItem('lastKnownDailyCountDate', todayUTC); // Store the date for validation
        localStorage.setItem('lastKnownCountTimestamp', Date.now().toString());
        
        // Force update state with explicit values
        setVisitorCount(realCount);
        setDailyCount(realDaily);
        
        // Log state updates
        console.log('Setting state - Daily:', realDaily);
        
        setBackendAvailable(true);
        setIsLoading(false);
      } catch (error) {
        console.warn('Error fetching visitor data (using fallback):', error.message || 'Network error');
        
        // Fallback: Use stored count if available AND it's from today (UTC)
        const lastKnownCount = localStorage.getItem('lastKnownVisitorCount');
        const lastKnownDaily = localStorage.getItem('lastKnownDailyCount');
        const lastKnownDailyDate = localStorage.getItem('lastKnownDailyCountDate');
        const todayUTC = getISTDateString();
        
        if (lastKnownCount) {
          const storedCount = parseInt(lastKnownCount, 10);
          // Only use stored daily count if it's from today (UTC)
          // If date doesn't match, use 0 for new day
          const storedDaily = (lastKnownDailyDate === todayUTC) 
            ? parseInt(lastKnownDaily, 10) || 0 
            : 0;
          setVisitorCount(storedCount);
          setDailyCount(storedDaily);
        } else {
          setVisitorCount(0);
          setDailyCount(0);
        }
        
        setBackendAvailable(false);
        setIsLoading(false);
      }
    };

    const startAutoRefresh = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(() => {
        const now = Date.now();
        const timeSinceLastActivity = now - lastActivity;
        
        // Only refresh if tab is visible and user has been active recently
        if (isTabVisible && timeSinceLastActivity < INACTIVITY_THRESHOLD) {
          fetchVisitorData();
        }
      }, 60000); // 60 seconds
    };

    const stopAutoRefresh = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Track user activity
    const updateActivity = () => {
      lastActivity = Date.now();
      if (!intervalId) {
        startAutoRefresh();
      }
    };

    // Handle tab visibility changes
    const handleVisibilityChange = () => {
      isTabVisible = !document.hidden;
      if (isTabVisible) {
        fetchVisitorData(); // Refresh immediately when tab becomes visible
        startAutoRefresh();
      } else {
        stopAutoRefresh();
      }
    };

    // Initial fetch
    fetchVisitorData();
    
    // Start auto-refresh
    startAutoRefresh();

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('mousedown', updateActivity);
    document.addEventListener('mousemove', updateActivity);
    document.addEventListener('keypress', updateActivity);
    document.addEventListener('scroll', updateActivity);
    document.addEventListener('touchstart', updateActivity);

    // Cleanup
    return () => {
      stopAutoRefresh();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('mousedown', updateActivity);
      document.removeEventListener('mousemove', updateActivity);
      document.removeEventListener('keypress', updateActivity);
      document.removeEventListener('scroll', updateActivity);
      document.removeEventListener('touchstart', updateActivity);
    };
  }, []);

  // Fetch website last updated date
  useEffect(() => {
    const fetchWebsiteLastUpdated = async () => {
      try {
        // Use the same API base URL as the rest of the application
        const API_BASE = process.env.NODE_ENV === 'production' 
          ? 'https://adminconsole.mlwb.in/api' 
          : 'http://localhost:5000/api';
        // Add timestamp to prevent caching
        const timestamp = new Date().getTime();
        const response = await fetch(`${API_BASE}/website-settings?t=${timestamp}`, { 
          cache: 'no-store'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.lastUpdatedDate) {
            const dateValue = new Date(data.lastUpdatedDate);
            setWebsiteLastUpdated(dateValue);
            // Store in localStorage as fallback for next page load
            localStorage.setItem('lastKnownWebsiteLastUpdated', data.lastUpdatedDate);
            localStorage.setItem('lastKnownWebsiteLastUpdatedTimestamp', Date.now().toString());
          } else {
            // Use fallback from localStorage if API returns null
            const storedDate = localStorage.getItem('lastKnownWebsiteLastUpdated');
            if (storedDate) {
              setWebsiteLastUpdated(new Date(storedDate));
            } else {
              setWebsiteLastUpdated(null);
            }
          }
        } else {
          // Use fallback from localStorage on API error
          const storedDate = localStorage.getItem('lastKnownWebsiteLastUpdated');
          if (storedDate) {
            setWebsiteLastUpdated(new Date(storedDate));
          } else {
            setWebsiteLastUpdated(null);
          }
        }
      } catch (error) {
        console.warn('Error fetching website last updated date:', error);
        // Use fallback from localStorage on network error
        const storedDate = localStorage.getItem('lastKnownWebsiteLastUpdated');
        if (storedDate) {
          setWebsiteLastUpdated(new Date(storedDate));
        } else {
          setWebsiteLastUpdated(null);
        }
      }
    };

    // Load from localStorage first for immediate display
    const storedDate = localStorage.getItem('lastKnownWebsiteLastUpdated');
    if (storedDate) {
      setWebsiteLastUpdated(new Date(storedDate));
    }
    
    // Then fetch fresh data from API (only on page load/refresh)
    fetchWebsiteLastUpdated();
  }, []);

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          {/* Organization Info */}
          <div className="footer-section">
            <h3 className="footer-title">
              {isMarathi() ? 'महाराष्ट्र कामगार कल्याण मंडळ' : 'Maharashtra Labour Welfare Board'}
            </h3>
            <p className="footer-description">
              {isMarathi() 
                ? 'कामगारांच्या कल्याणासाठी समर्पित संस्था' 
                : 'Dedicated to the welfare of Labours'
              }
            </p>
          </div>

          {/* Other Links */}
          <div className="footer-section" data-read-section="footer-other-links">
            <h4 className="footer-subtitle">
              {isMarathi() ? 'इतर लिंक्स्' : 'Other Links'}
            </h4>
            <ul className="footer-links">
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => window.open('https://public.mlwb.in/public', '_blank', 'noopener,noreferrer')}
                >
                  {isMarathi() 
                    ? 'महाराष्ट्र कामगार कल्याण मंडळ सार्वजनिक पोर्टल' 
                    : 'Maharashtra Labour Welfare Board Public Portal'
                  }
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => handleExternalLink('https://www.india.gov.in/')}
                >
                  {isMarathi() 
                    ? 'भारताचे राष्ट्रीय पोर्टल' 
                    : 'National Portal of India'
                  }
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => handleExternalLink('https://maharashtra.gov.in/home/index')}
                >
                  {isMarathi() 
                    ? 'महाराष्ट्र शासन अधिकृत संकेतस्थळ' 
                    : 'Official Website of Maharashtra Government'
                  }
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => handleExternalLink('https://mahadish.in/marathi/home')}
                >
                  {isMarathi() 
                    ? 'औद्योगिक सुरक्षा व आरोग्य संचालनालय' 
                    : 'Directorate of Industrial Safety and Health'
                  }
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => handleExternalLink('https://vvgnli.gov.in/')}
                >
                  {isMarathi() 
                    ? 'वी. वी. गिरि राष्ट्रीय श्रम संस्थान' 
                    : 'V. V. Giri National Labour Institute'
                  }
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => handleExternalLink('https://mahabocw.in/')}
                >
                  {isMarathi() 
                    ? 'महाराष्ट्र इमारत व इतर बांधकाम कामगार कल्याणकारी मंडळ' 
                    : 'Maharashtra Building and Other Construction Workers Welfare Board'
                  }
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => handleExternalLink('https://aaplesarkar.mahaonline.gov.in')}
                >
                  {isMarathi() 
                    ? 'आपले सरकार' 
                    : 'Aaple Sarkar'
                  }
                </button>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div className="footer-section" data-read-section="footer-quick-links">
            <h4 className="footer-subtitle">
              {isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links'}
            </h4>
            <ul className="footer-links">
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => window.open('https://adminconsole.mlwb.in/', '_blank', 'noopener,noreferrer')}
                >
                  {isMarathi() ? 'प्रशासक लॉगिन' : 'Admin Login'}
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => navigate('/sitemap')}
                >
                  {isMarathi() ? 'साइट मॅप' : 'Site Map'}
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => navigate('/faq')}
                >
                  {isMarathi() ? 'प्रश्नोत्तरे' : 'FAQ'}
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => navigate('/feedback')}
                >
                  {isMarathi() ? 'अभिप्राय' : 'Feedback'}
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => navigate('/grievance')}
                >
                  {isMarathi() ? 'तक्रार निवारण' : 'Grievance'}
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => navigate('/privacy')}
                >
                  {isMarathi() ? 'गोपनीयता धोरण आणि अस्वीकरण' : 'Privacy Policy and Disclaimer'}
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => navigate('/screen-reader')}
                >
                  {isMarathi() ? 'स्क्रीन रीडर' : 'Screen Reader'}
                </button>
              </li>
              <li>
                <button 
                  type="button"
                  className="footer-link"
                  onClick={() => navigate('/contact-sop')}
                >
                  {isMarathi() ? 'संपर्क - SOP' : 'Contact Us - SOP'}
                </button>
              </li>
            </ul>
          </div>

          {/* Social Media */}
          <div className="footer-section" data-read-section="footer-social-media">
            <h4 className="footer-subtitle">
              {isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media'}
            </h4>
            <div className="social-media">
              <p className="social-description">
                {isMarathi() 
                  ? 'आमच्याशी जोडा' 
                  : 'Stay connected with us'
                }
              </p>
              <div className="social-icons">
                <a href="https://x.com/mhlabourwelfare" className="social-link" aria-label="X" target="_blank" rel="noopener noreferrer">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a href="https://www.facebook.com/mhlabourwelfare" className="social-link" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                </a>
                <a href="https://www.youtube.com/@mhlabourwelfare" className="social-link" aria-label="YouTube" target="_blank" rel="noopener noreferrer">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
                    <polygon points="9.75,15.02 15.5,11.75 9.75,8.48"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/mhlabourwelfare" className="social-link" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="https://web.whatsapp.com" className="social-link" aria-label="WhatsApp" target="_blank" rel="noopener noreferrer">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Visitor Counter - Backend or Fallback */}
        {isLoading ? (
          <div className="horizontal-visitor-counter" data-read-section="footer-visitor-count">
            <div className="visitor-stats">
              <div className="stat-item">
                <div className="stat-number">0</div>
                <div className="stat-label">{isMarathi() ? 'एकूण भेटी' : 'Total Visits'}</div>
              </div>
              <div className="stat-separator"></div>
              <div className="stat-item">
                <div className="stat-number daily">0</div>
                <div className="stat-label">{isMarathi() ? 'दैनिक भेटी' : 'Daily Visits'}</div>
              </div>
              <div className="stat-separator"></div>
              <div className="stat-item">
                <div className="stat-label last-update">
                  {isMarathi() ? 'लोड होत आहे...' : 'Loading...'}
                </div>
              </div>
            </div>
            {websiteLastUpdated && (
              <p className="website-last-updated-text" data-read-section="footer-last-updated">
                {isMarathi() 
                  ? `वेबसाइट अंतिम अपडेट: ${formatDateMarathi(websiteLastUpdated)}` 
                  : `Website Last Updated: ${formatDateEnglish(websiteLastUpdated)}`
                }
              </p>
            )}
          </div>
        ) : backendAvailable ? (
          <div className="horizontal-visitor-counter" data-read-section="footer-visitor-count">
            <div className="visitor-stats">
              <div className="stat-item">
                <div className="stat-number">{visitorCount.toLocaleString()}</div>
                <div className="stat-label">{isMarathi() ? 'एकूण भेटी' : 'Total Visits'}</div>
              </div>
              <div className="stat-separator"></div>
              <div className="stat-item">
                <div className="stat-number daily">{dailyCount.toLocaleString()}</div>
                <div className="stat-label">{isMarathi() ? 'दैनिक भेटी' : 'Daily Visits'}</div>
              </div>
            </div>
            {websiteLastUpdated && (
              <p className="website-last-updated-text" data-read-section="footer-last-updated">
                {isMarathi() 
                  ? `वेबसाइट अंतिम अपडेट: ${formatDateMarathi(websiteLastUpdated)}` 
                  : `Website Last Updated: ${formatDateEnglish(websiteLastUpdated)}`
                }
              </p>
            )}
          </div>
        ) : (
          <FallbackVisitorCounter />
        )}

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p className="copyright">
              © {new Date().getFullYear()} {isMarathi() 
                ? 'महाराष्ट्र कामगार कल्याण मंडळ. सर्व हक्क राखीव.' 
                : 'Maharashtra Labour Welfare Board. All rights reserved.'
              }
            </p>
            <div className="developer-credit">
              <p className="developer-text">
                A digital experience by <a href="https://bwitonline.com/" target="_blank" rel="noopener noreferrer" className="company-name">Blue Windows Infotech</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
